/**
 * Authentication store (Zustand v5).
 *
 * Manages the full auth lifecycle: token storage, silent refresh,
 * JWT decode for user context, and logout.
 *
 * Cross-References:
 *   - FR-AUTH-001: Authentication state management
 *   - FR-AUTH-003: Token handling
 *   - api-specifications/foundation.md §1: Auth endpoints
 */
import { create } from 'zustand';

/* ── types ─────────────────────────────────────────────────── */

interface User {
  id: string;
  email: string;
  displayName: string;
  tenantId: string;
  roleId: string | null;
  permissions: string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  /** Called from <Callback /> after OIDC redirect. Returns true if session-select is needed. */
  handleCallback: (accessToken: string) => Promise<boolean>;

  /** Replace the current access token (e.g. after context switch). */
  setAccessToken: (accessToken: string) => void;

  /** Silently refresh the access token via the httpOnly refresh cookie. */
  refreshToken: () => Promise<boolean>;

  /** Log out: revoke session server-side and clear local state. */
  logout: () => Promise<void>;

  /** Restore session on app mount (attempt silent refresh). */
  initialise: () => Promise<void>;

  setLoading: (loading: boolean) => void;
  clearAuth: () => void;
}

/* ── JWT payload decode (no verification — server does that) ─ */

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1];
    if (!base64) return null;
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function userFromToken(token: string): User | null {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.sub !== 'string') return null;
  return {
    id: payload.sub as string,
    email: (payload.email as string) ?? '',
    displayName: (payload.name as string) ?? '',
    tenantId: (payload.tid as string) ?? '',
    roleId: (payload.rid as string) ?? null,
    permissions: (payload.permissions as string[]) ?? [],
  };
}

/* ── store ─────────────────────────────────────────────────── */

export const useAuthStore = create<AuthState>()((set, get) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true, // start true so app shows loader until initialise() completes

  handleCallback: async (accessToken: string) => {
    const user = userFromToken(accessToken);
    if (!user) throw new Error('Invalid token');
    set({ user, accessToken, isAuthenticated: true, isLoading: false });

    // If the token has no tenant context yet, the UI should navigate
    // to /select-session so the user picks one.
    return !user.tenantId;
  },

  setAccessToken: (accessToken: string) => {
    const user = userFromToken(accessToken);
    set({ user, accessToken, isAuthenticated: !!user, isLoading: false });
  },

  refreshToken: async () => {
    try {
      const res = await fetch('/api/v1/auth/refresh', {
        method: 'POST',
        credentials: 'include', // sends the httpOnly refresh cookie
      });
      if (!res.ok) return false;

      const json = await res.json();
      // Backend wraps in { data: { access_token, expires_in } }
      const payload = json.data ?? json;
      const accessToken = payload.access_token as string;
      if (!accessToken) return false;

      const user = userFromToken(accessToken);
      set({
        user,
        accessToken,
        isAuthenticated: !!user,
      });
      return true;
    } catch {
      return false;
    }
  },

  logout: async () => {
    const { accessToken } = get();
    // Best-effort server revocation
    try {
      await fetch('/api/v1/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : undefined,
      });
    } catch {
      // Proceed with local cleanup regardless
    }
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    });
  },

  initialise: async () => {
    set({ isLoading: true });
    await get().refreshToken();
    set({ isLoading: false });
  },

  setLoading: (isLoading) => set({ isLoading }),
  clearAuth: () =>
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
    }),
}));
